import React, { forwardRef, useMemo, Fragment, useContext } from "react";
import { Grid, useTheme, Theme } from "@material-ui/core";
import { addDays, differenceInSeconds, parseISO } from "date-fns";
import classnames from "classnames";
import { Identifier, ReactAdminComponentProps } from "react-admin";
import { useSelector } from "react-redux";
import { AppState } from "../../../../common/types";
import { from } from "manipula";
import { timespan } from "../../../../common/util/timespan";
import { DriverLogDetailedView } from "../../../../common/data/service-proxies";
import { addSeconds } from "date-fns/esm";
import { EventHoverContext } from "./HoverContext";
import { HosEventLine } from "./HosEventLine";
import { getEventGraphHeight, getPreviousDutyStatus } from "./graphUtils";

interface HosGraphViewCanvasProps extends ReactAdminComponentProps {
  record: DriverLogDetailedView;
  onHover: (id?: Identifier) => void;
  onEventClick: (id: Identifier) => void;
  hoveredViolationId?: Identifier;
  cycleBreak?: string | null;
  drawRefuels?: boolean;
}

const shouldHover = (currentEvent: Identifier, hoveredEventId?: Identifier) =>
  currentEvent === hoveredEventId;

export const HosGraphViewCanvas = forwardRef<
  SVGSVGElement,
  HosGraphViewCanvasProps
>(
  (
    {
      record,
      onHover,
      onEventClick,
      hoveredViolationId,
      cycleBreak,
      drawRefuels,
    },
    svgElement
  ) => {
    const theme: Theme = useTheme();
    const themeMode = useSelector((x: AppState) => x.theme);
    const { hoveredEventId } = useContext(EventHoverContext);
    const cycleBreakTime = !!cycleBreak
      ? differenceInSeconds(parseISO(cycleBreak), parseISO(record.startOfDay!))
      : undefined;

    const refuels = useMemo(
      () =>
        drawRefuels
          ? (record.refuels ?? []).map((x) => ({
              color: x.isFromProvider
                ? theme.palette.success.main
                : theme.palette.primary.main,
              time: parseISO(x.time!),
              second: differenceInSeconds(
                parseISO(x.time!),
                parseISO(record.startOfDay!)
              ),
            }))
          : [],
      [record, drawRefuels, theme]
    );

    const basicEvents = useMemo(
      () =>
        (record.events ?? [])
          .filter((x) => x.isBasic)
          .map((x) => {
            const lineColor =
              x.origin === "AutomaticallyRecordedByEld"
                ? theme.palette.success.main
                : theme.palette.getContrastText(theme.palette.background.paper);
            return {
              ...x,
              lineColor,
            };
          }),
      [record, theme]
    );

    let maxTime: Date | undefined;
    const hoursToday = record.hoursToday ?? 24;
    if (record?.startOfDay) {
      maxTime = addSeconds(addDays(parseISO(record.startOfDay), 1), -1);
      if (maxTime > new Date()) {
        maxTime = new Date();
      }
    }

    return (
      <>
        <style>
          {`rect.hos-event-highlight, rect.hos-violation-highlight {
  fill-opacity: 0;
}

rect.hos-event-highlight-hovered-dark,
rect.hos-event-highlight-hovered-light,
rect.hos-violation-highlight-hovered-dark,
rect.hos-violation-highlight-hovered-light {
  fill-opacity: 0.1;
}

div.hos-event-highlight-hovered-dark {
  background-color: rgba(255, 255, 255, 0.12) !important;
}

div.hos-event-highlight-hovered-light {
  background-color: rgba(0, 0, 0, 0.12) !important;
}

.hos-event, .hos-violation {
  stroke-width: 4px;
}

.hos-event-transition {
  stroke-width: 1px;
}

@media print {
  .violations-display {
    display: none;
  }
}
`}
        </style>
        <Grid
          className="hos-graph-view-canvas-container"
          item
          xl={12}
          lg={12}
          md={12}
          sm={12}
          xs={12}>
          <svg ref={svgElement} viewBox={`0 0 ${100 + 50 * hoursToday} 210`}>
            <symbol
              id="LocalGasStationIcon"
              xmlns="http://www.w3.org/2000/svg"
              height="24"
              viewBox="0 0 24 24"
              width="24">
              <path d="M0 0h24v24H0z" fill="none" />
              <path d="m19.77 7.23.01-.01-3.72-3.72L15 4.56l2.11 2.11c-.94.36-1.61 1.26-1.61 2.33 0 1.38 1.12 2.5 2.5 2.5.36 0 .69-.08 1-.21v7.21c0 .55-.45 1-1 1s-1-.45-1-1V14c0-1.1-.9-2-2-2h-1V5c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v16h10v-7.5h1.5v5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V9c0-.69-.28-1.32-.73-1.77zM12 10H6V5h6v5zm6 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
            </symbol>
            <defs>
              <pattern
                id="background"
                width={`${(50 / (50 * hoursToday)) * 100}%`}
                height="100%">
                <rect
                  fill={theme.palette.background.paper}
                  x="0"
                  y="0"
                  width="50"
                  height="160"
                />
                <g
                  className="vertical-lines"
                  style={{
                    stroke: theme.palette.text.disabled,
                    strokeWidth: 0.8,
                  }}>
                  <line x1="0" y1="0" x2="0" y2="160" />
                  <g id="rows" style={{ strokeWidth: 0.5 }}>
                    <g id="row-1">
                      <line x1="12.5" y1="0" x2="12.5" y2="20" />
                      <line x1="25" y1="0" x2="25" y2="30" />
                      <line x1="37.5" y1="0" x2="37.5" y2="20" />
                    </g>
                    <g id="row-2">
                      <line x1="12.5" y1="40" x2="12.5" y2="60" />
                      <line x1="25" y1="40" x2="25" y2="70" />
                      <line x1="37.5" y1="40" x2="37.5" y2="60" />
                    </g>
                    <g id="row-3">
                      <line x1="12.5" y1="100" x2="12.5" y2="120" />
                      <line x1="25" y1="90" x2="25" y2="120" />
                      <line x1="37.5" y1="100" x2="37.5" y2="120" />
                    </g>
                    <g id="row-4">
                      <line x1="12.5" y1="140" x2="12.5" y2="160" />
                      <line x1="25" y1="130" x2="25" y2="160" />
                      <line x1="37.5" y1="140" x2="37.5" y2="160" />
                    </g>
                  </g>
                </g>
                <g
                  className="horizontal-lines"
                  style={{
                    stroke: theme.palette.text.disabled,
                    strokeWidth: 0.8,
                  }}>
                  <line x1="0" y1="0" x2="50" y2="0" />
                  <line x1="0" y1="40" x2="50" y2="40" />
                  <line x1="0" y1="80" x2="50" y2="80" />
                  <line x1="0" y1="120" x2="50" y2="120" />
                  <line
                    style={{ strokeWidth: 2 }}
                    x1="0"
                    y1="160"
                    x2="50"
                    y2="160"
                  />
                </g>
              </pattern>
            </defs>
            <rect
              id="frame"
              x="50"
              y="50"
              width={`${50 * hoursToday}`}
              height="160"
              style={{
                strokeWidth: 1,
                stroke: theme.palette.text.disabled,
              }}></rect>
            <g
              id="hours"
              style={{
                fontFamily: theme.typography.h6.fontFamily,
                fontSize: theme.typography.h6.fontSize,
                fill: theme.palette.text.secondary,
              }}>
              {[...Array(hoursToday + 1)].map((_, i) => (
                <text key={i} y="40" x={`${(i % 12 < 10 ? 45 : 40) + 50 * i}`}>
                  {hoursToday === 24 ? (i === 12 ? "N" : i % 12 || "M") : null}
                  {hoursToday === 25
                    ? i === 3
                      ? ""
                      : i > 2
                      ? (24 - hoursToday + i) % 12 || "M"
                      : i % 12 || "M"
                    : null}
                  {hoursToday === 23
                    ? i > 1
                      ? (24 - hoursToday + i) % 12 || "M"
                      : i % 12 || "M"
                    : null}
                </text>
              ))}
            </g>
            <g
              id="statuses"
              style={{
                fontFamily: theme.typography.body1.fontFamily,
                fontSize: theme.typography.body1.fontSize,
                fill: theme.palette.text.secondary,
              }}>
              <text y="75" x="10">
                OF
              </text>
              <text y="115" x="10">
                SB
              </text>
              <text y="155" x="10">
                DR
              </text>
              <text y="195" x="10">
                ON
              </text>
            </g>
            <g
              id="durations"
              style={{
                fontFamily: theme.typography.body1.fontFamily,
                fontSize: theme.typography.body1.fontSize,
                fill: theme.palette.text.secondary,
              }}>
              <text y="75" x={`${50 * hoursToday + 60}`}>
                {timespan.format(
                  "h:m",
                  from(basicEvents ?? [])
                    .where(
                      (x: any) =>
                        x.dutyStatus === "ChangeToOffDutyStatus" ||
                        x.dutyStatus ===
                          "DriverIndicationAuthorizedPersonalUseCmv"
                    )
                    .sum((x: any) => x.durationInSeconds * 1000)
                )}
              </text>
              <text y="115" x={`${50 * hoursToday + 60}`}>
                {timespan.format(
                  "h:m",
                  from(basicEvents ?? [])
                    .where(
                      (x: any) => x.dutyStatus === "ChangeToSleeperBerthStatus"
                    )
                    .sum((x: any) => x.durationInSeconds * 1000)
                )}
              </text>
              <text y="155" x={`${50 * hoursToday + 60}`}>
                {timespan.format(
                  "h:m",
                  from(basicEvents ?? [])
                    .where((x: any) => x.dutyStatus === "ChangeToDrivingStatus")
                    .sum((x: any) => x.durationInSeconds * 1000)
                )}
              </text>
              <text y="195" x={`${50 * hoursToday + 60}`}>
                {timespan.format(
                  "h:m",
                  from(basicEvents ?? [])
                    .where(
                      (x: any) =>
                        x.dutyStatus === "ChangeToOnDutyNotDrivingStatus" ||
                        x.dutyStatus === "DriverIndicationYardMoves"
                    )
                    .sum((x: any) => x.durationInSeconds * 1000)
                )}
              </text>
            </g>
            <rect
              x="50"
              y="50"
              height="160"
              width={`${50 * hoursToday}`}
              fill="url(#background)"
            />
            <g id="hos-graph">
              {basicEvents.map(
                (
                  { id, dutyStatus, startTime, endTime, lineColor },
                  index: number
                ) => {
                  return (
                    <HosEventLine
                      hover={shouldHover(id!, hoveredEventId)}
                      key={id}
                      id={id!}
                      index={index}
                      dutyStatus={dutyStatus}
                      startTime={startTime!}
                      endTime={endTime!}
                      lineColor={lineColor}
                      basicEvents={basicEvents}
                      startOfDay={record.startOfDay}
                      offsetX={50}
                      onClick={() => onEventClick && id && onEventClick(id)}
                      onMouseEnter={() => onHover && onHover(id)}
                      onMouseLeave={() => onHover && onHover(undefined)}
                    />
                  );
                }
              )}
            </g>
            {record?.violations && record.violations.length ? (
              <g id="hos-violations">
                {record.violations.map((violation) => {
                  if (!record.startOfDay) {
                    return null;
                  }

                  if (!violation.isHosViolation) {
                    return null;
                  }

                  const violationStartTime = parseISO(
                    violation.startDateTime ?? record.startOfDay
                  );
                  const violationStartSeconds = differenceInSeconds(
                    violationStartTime,
                    parseISO(record.startOfDay)
                  );

                  const violationEndTime = violation.endDateTime
                    ? parseISO(violation.endDateTime)
                    : maxTime!;
                  const violationEndSeconds = differenceInSeconds(
                    violationEndTime,
                    parseISO(record.startOfDay)
                  );

                  const basicEventsInViolation = basicEvents.filter(
                    (basicEvent) => {
                      const eventEndTime = basicEvent.endTime
                        ? parseISO(basicEvent.endTime)
                        : maxTime!;
                      const eventStartTime = basicEvent.startTime
                        ? parseISO(basicEvent.startTime)
                        : parseISO(record.startOfDay!);
                      return (
                        eventEndTime > violationStartTime &&
                        eventStartTime < violationEndTime
                      );
                    }
                  );

                  return basicEventsInViolation.map((basicEvent, index) => {
                    let previousY: number | null = null;
                    const prevDutyStatus = getPreviousDutyStatus(
                      basicEvents,
                      index
                    );
                    if (prevDutyStatus) {
                      previousY = getEventGraphHeight(prevDutyStatus);
                    }

                    let startSeconds = differenceInSeconds(
                      parseISO(basicEvent.startTime!),
                      parseISO(record.startOfDay!)
                    );
                    if (startSeconds < violationStartSeconds) {
                      startSeconds = violationStartSeconds;
                    }

                    let endSeconds = differenceInSeconds(
                      parseISO(basicEvent.endTime!),
                      parseISO(record.startOfDay!)
                    );
                    if (endSeconds > violationEndSeconds) {
                      endSeconds = violationEndSeconds;
                    }

                    const startingPoint = 50 + (50 * startSeconds) / 3600;
                    const endingPoint = 50 + (50 * endSeconds) / 3600;
                    let dashArray: string | undefined;
                    const y = getEventGraphHeight(basicEvent.dutyStatus!);
                    if (
                      basicEvent.dutyStatus === "PC" ||
                      basicEvent.dutyStatus === "YM"
                    ) {
                      dashArray = "1,2";
                    }

                    return (
                      y && (
                        <Fragment key={basicEvent.id}>
                          <line
                            className="hos-violation"
                            x1={startingPoint}
                            x2={endingPoint}
                            y1={y}
                            y2={y}
                            style={{
                              stroke: theme.palette.error.main,
                              strokeDasharray: dashArray,
                            }}
                          />
                          <rect
                            className={
                              hoveredViolationId === violation.id
                                ? classnames(
                                    "hos-violation-highlight",
                                    `hos-violation-highlight-hovered-${themeMode}`
                                  )
                                : "hos-violation-highlight"
                            }
                            x={startingPoint}
                            y={50}
                            height={160}
                            width={Math.abs(endingPoint - startingPoint)}
                            style={{ fill: theme.palette.error.main }}
                            onMouseEnter={() =>
                              onHover && onHover(basicEvent.id)
                            }
                            onMouseLeave={() => onHover && onHover(undefined)}
                          />
                          {previousY && (
                            <line
                              className="hos-event-transition"
                              x1={startingPoint}
                              x2={startingPoint}
                              y1={previousY > y ? previousY + 2 : previousY - 2}
                              y2={previousY > y ? y - 2 : y + 2}
                              style={{
                                stroke: theme.palette.error.main,
                              }}
                            />
                          )}
                        </Fragment>
                      )
                    );
                  });
                })}
              </g>
            ) : null}
            {!!cycleBreakTime ? (
              <g id="cycle-break">
                <line
                  x1={50 + (50 * cycleBreakTime) / 3600}
                  x2={50 + (50 * cycleBreakTime) / 3600}
                  y1={50}
                  y2={250}
                  style={{
                    stroke: theme.palette.info.main,
                    strokeWidth: "3",
                    strokeDasharray: "10, 5",
                  }}
                />
                <ellipse
                  cx={50 + (50 * cycleBreakTime) / 3600}
                  cy={60}
                  rx={14}
                  ry={12}
                  style={{ fill: theme.palette.info.main }}
                />
                <text
                  x={42 + (50 * cycleBreakTime) / 3600}
                  y={65}
                  style={{ fill: theme.palette.common.white }}>
                  34
                </text>
              </g>
            ) : null}
            <g id="refuels">
              {refuels.map((x) => (
                <>
                  <line
                    x1={50 + (50 * x.second) / 3600}
                    x2={50 + (50 * x.second) / 3600}
                    y1={50}
                    y2={250}
                    style={{
                      stroke: x.color,
                      strokeWidth: "3",
                      strokeDasharray: "10, 5",
                    }}
                  />
                  <ellipse
                    cx={50 + (50 * x.second) / 3600}
                    cy={60}
                    rx={14}
                    ry={12}
                    style={{ fill: x.color }}
                  />
                  <use
                    href="#LocalGasStationIcon"
                    x={43 + (50 * x.second) / 3600}
                    y={52}
                    width="16"
                    height="16"
                    fill={theme.palette.getContrastText(x.color)}
                  />
                </>
              ))}
            </g>
          </svg>
        </Grid>
      </>
    );
  }
);
